/**
 * The call a ticket came out of, as one clickable line.
 *
 * The other half of the link the call pane draws back this way, and the reason
 * both exist: a voicemail ticket is a transcript with no voice in it. An agent
 * reading "the caller wants a quote for a workshop site" often needs to hear how
 * it was said, or catch a number the transcript rendered as words - and until
 * this was here the only route was to remember the caller's number, open the
 * call section, and search for it.
 *
 * Deliberately not a repeat of the ticket's own content. The transcript is
 * already the first message in the thread and the summary is already the
 * internal note under it; this says which call, when, how long, and whether
 * there is audio at all - the four things that decide whether opening it is
 * worth the click.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight, Mic, PhoneIncoming, Voicemail } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { formatPhone } from "../../../lib/phone";
import type { TicketCallRef } from "../../../lib/support";
import { StatusPill, fmtDateTime, fmtDuration } from "../../ui";

export function TicketCallBadge({ call }: { call: TicketCallRef }) {
  const t = useT();
  // The other party, never a fixed column: on an inbound call `toNumber` is our
  // own line, and printing that on the ticket would name the office as the
  // person who rang.
  const other = call.direction === "inbound" ? call.fromNumber : call.toNumber;

  return (
    <Link
      to={`/calls/${call.id}`}
      className="flex items-center gap-3 rounded-2xl bg-[var(--crm-sunk)] px-4 py-3 transition hover:bg-[var(--crm-surface)]"
    >
      <span className="flex size-9 flex-none items-center justify-center rounded-full bg-[var(--crm-surface)] text-muted-foreground">
        {call.voicemail ? <Voicemail className="size-4" /> : <PhoneIncoming className="size-4" />}
      </span>

      <span className="min-w-0 flex-1">
        <span className="crm-prow-n crm-trunc block">
          {other ? formatPhone(other) : t("calls.incoming.withheld")}
        </span>
        <span className="crm-prow-s crm-trunc crm-tnum block">
          {t(call.voicemail ? "calls.pane.voicemail" : "calls.pane.inboundCall")}
          {" · "}
          {fmtDateTime(call.startedAt)}
          {call.durationSec ? ` · ${fmtDuration(call.durationSec)}` : ""}
        </span>
      </span>

      {/* Whether there is anything to listen to. A purged or never-stored
          recording is the common case on an old call, and finding that out after
          the click is worse than being told here. */}
      {call.recordingStatus === "stored" ? (
        <StatusPill tone="ok">
          <Mic className="size-3" />
          {t("calls.pane.recorded")}
        </StatusPill>
      ) : null}

      <ArrowUpRight className="size-4 flex-none text-muted-foreground" />
    </Link>
  );
}
